/**
* @render react
* @name Price
*/
import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { withPolaris } from 'Components/protons/Polaris';

/**
 * Price component
 * @param {Object} props - the props object
 * @param {number} props.min - starting price range
 * @param {number} props.max - ending price range
 * @param {boolean} props.minOnly - Whether to apply the class names for styling.
 * @param {string} props.prefixText - text to prepend to the price
 * @param {string} props.postText - text to append to the price
 * @param {string} props.currency - the currency used for the price - default £
 * @param {number} props.decimals - the currency used for the price - default £
 * @param {string} props.separator - the currency used for the price - default £
 * @param {string} props.decimalChar - the currency used for the price - default £
 * @param {boolean} props.loadStyles - Whether to apply the class names for styling.
 * @param {Object} props.extraClassNames - extra classes to be applied to the component
 * @param {Object} props.context - Extra information coming from Polaris provider.
 */
const Price = (
  {
    min,
    max,
    minOnly,
    prefixText,
    postText,
    currency,
    decimals,
    separator,
    decimalChar,
    loadStyles,
    extraClassNames,
    priceSeparator,
    context: { cssPrefix },
  },
) => {
  const formatNumber = number => number
    .toFixed(decimals) // Convert to fixed decimals.
    .replace('.', decimalChar)
    .replace(/\B(?=(\d{3})+(?!\d))/g, separator); // Regex to split apart numbers in threes.

  const formatPrice = () => {
    if (!minOnly) {
      if (min && max) {
        return (
          <>
            {`${currency}${formatNumber(min)}`}
            {' '}
            <span className={`${cssPrefix}__price--separator`}>{priceSeparator}</span>
            {' '}
            {`${currency}${formatNumber(max)}`}
          </>
        );
      }

      if (max) {
        return `${currency}${formatNumber(max)}`;
      }

      if (min) {
        return `${currency}${formatNumber(min)}`;
      }
    } else if (min) {
      return `${currency}${formatNumber(min)}`;
    }

    return 'Unknown';
  };

  return (
    <span
      className={classNames({
        [`${cssPrefix}__price`]: loadStyles,
        ...extraClassNames,
      })}
    >
      { ((prefixText && !max) || (prefixText && minOnly)) && (
        <span className={`${cssPrefix}__price--prefix`}>
          { prefixText }
        </span>
      )}

      <span className={`${cssPrefix}__price--price`}>{ formatPrice() }</span>

      { postText && (
        <span className={`${cssPrefix}__price--post`}>
          { postText }
        </span>
      )}
    </span>
  );
};

/**
 * Valid props
 */
Price.propTypes = {
  min: PropTypes.number,
  max: PropTypes.number,
  minOnly: PropTypes.bool,
  prefixText: PropTypes.string,
  postText: PropTypes.string,
  currency: PropTypes.string,
  decimals: PropTypes.number,
  separator: PropTypes.string,
  decimalChar: PropTypes.string,
  priceSeparator: PropTypes.string,
  loadStyles: PropTypes.bool,
  context: PropTypes.shape(),
  extraClassNames: PropTypes.shape(),
};

/**
 * Default props
 */
Price.defaultProps = {
  min: null,
  max: null,
  minOnly: false,
  prefixText: '',
  postText: '',
  currency: '£',
  decimals: 0,
  separator: ',',
  decimalChar: '.',
  priceSeparator: '—',
  loadStyles: true,
  context: {},
  extraClassNames: {},
};

export default withPolaris(Price);
